import useToggle from '_hooks/useToggle';
import useUploadImage from '../../hooks/autoFillAIHooks/useUploadImage';
import useDetectEventImg from '../../hooks/autoFillAIHooks/useDetectEventImg';
import useApplyEventAIData from '../../hooks/autoFillAIHooks/useApplyEventAIData';
import './style.scss';

export default function AutoFillAI() {
  const [isOpen, toggleOpen] = useToggle(false);

  const {
    uploadedFile,
    fileInputRef,
    handleImageChange,
    handleRemoveFile,
  } = useUploadImage();

  const {
    isSubmitting,
    statusMessage,
    errorMessage,
    handleDetect,
    clearMessages,
  } = useDetectEventImg();

  const { applyEventData } = useApplyEventAIData();

  const onImageChange = (e) => {
    if (isSubmitting) return;
    clearMessages();
    handleImageChange(e);
  };

  const onRemoveFile = () => {
    clearMessages();
    handleRemoveFile();
  };

  const handleSubmit = async () => {
    const result = await handleDetect({ file: uploadedFile });
    if (result) {
      applyEventData(result);
    }
  };

  return (
    <div className="ai-fill-card">
      <div className="ai-fill-hd" onClick={toggleOpen}>
        <div className="ai-fill-icon">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74L12 2z" />
            <path d="M19 15l0.7 2.1L22 18l-2.3 0.9L19 21l-0.7-2.1L16 18l2.3-0.9L19 15z" />
            <path d="M5 13l0.5 1.5L7 15l-1.5 0.5L5 17l-0.5-1.5L3 15l1.5-0.5L5 13z" />
          </svg>
        </div>
        <div className="ai-fill-content">
          <div className="ai-fill-title">
            Quick Fill with AI <span className="ai-fill-badge">BETA</span>
          </div>
          <div className="ai-fill-sub">
            Upload a photo of a menu or poster — and we&apos;ll fill in the form for you.
          </div>
        </div>
        <div className={`ai-fill-chevron ${isOpen ? 'open' : ''}`}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="ai-fill-body">
          <div className="ai-fill-actions">
            <label className={`ai-fill-upload-btn ${isSubmitting ? 'disabled' : ''}`}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              Upload photo
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                disabled={isSubmitting}
                onChange={onImageChange}
              />
            </label>

            <button
              type="button"
              className="ai-fill-btn"
              disabled={isSubmitting || !uploadedFile}
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <svg className="ai-fill-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74L12 2z" />
                </svg>
              )}
              Fill form with AI
            </button>
          </div>

          {uploadedFile && (
            <div className="ai-fill-uploaded-files">
              <div className="ai-fill-image-chip">
                <span>{uploadedFile.name}</span>
                <button
                  type="button"
                  className="chip-remove-btn"
                  onClick={onRemoveFile}
                  title="Remove image"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {statusMessage && (
            <div className="ai-fill-status">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{statusMessage}</span>
            </div>
          )}

          {errorMessage && (
            <div className="ai-fill-error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{errorMessage}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
